import React, {useState, useEffect} from 'react';
import {useAuth0} from '@auth0/auth0-react';
import {useNavigate} from 'react-router-dom';
import ChIPAnalysisDownStreamStep0 from '../ChIPAnalysisDownStreamStep0';
import CreationFlowBar from '../../../../components/CreationFlowBar';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {Error as ErrorComponent} from '../../../../components/Error';
import {
    AssignmentOutlined,
    CheckCircleOutlineRounded,
    IntegrationInstructionsOutlined,
    SettingsOutlined,
    ListAltOutlined
} from '@mui/icons-material';
import {getAppConfig} from '../../../../utils/config';
import ExpandableTableChip from '../ExpandableTableChip';
import SelectVariabledDBA from './SelectVariablesDifferentialBindingAnalysis';
import DifferentialBindingParameters from './DifferentialBindingParameters';
import DBReview from './DifferentialBindingReview';
import {
    defaultChIPAnalysisbyExperiment,
    peakCallingsData,
    DefaultChIPAnalyses,
    createAnalysis,
    startAnalysis
} from 'src/utils/bff-communication';
import {expandSampleSelection} from 'src/utils/chip';

interface Step {
    name: string;
    caption: string;
    description: string;
    component: React.ReactNode;
    icon: React.ReactNode;
}

const FormContainer = styled(Box)({
    flex: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '990px',
    minWidth: '600px',
    padding: '1.5% 2%',
    gap: 10
});

interface CreateDifferentialBindingAnalysisProps {
    experiment: any;
    samples: any[];
}

export function CreateDifferentialBindingAnalysis({experiment, samples}: CreateDifferentialBindingAnalysisProps) {
    const {apiHost} = getAppConfig();
    const [ableToSave, setAbleToSave] = useState(false);
    const {getAccessTokenSilently} = useAuth0();
    const [error, setError] = useState<string>('');
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [groupLevels, setGroupLevels] = useState<{[s: string]: string[]}>({});
    const [defaultChIPOptions, setDefaultChIPOptions] = useState<DefaultChIPAnalyses[]>([]);
    const [warning, setWarning] = useState<string>('');

    const navigate = useNavigate();
    const samplesD = samples;

    const [formData, setFormData] = useState<DBFormData>({
        type: 'Differential Binding',
        name: '',
        description: '',
        sample_ids: [],
        group_options: [],
        selected_group_options: {},
        default_ChIP_analysis: '',
        differential_binding_analysis: {
            significance_threshold: 0.05,
            summits: 200,
            tss_region: 3000,
            level: 'transcript',
            overlap: 'tss',
            genomic_annotation_priority: ['Promoter', '5UTR', '3UTR', 'Exon', 'Intron', 'Downstream', 'Intergenic']
        }
    });

    /**
     * Formats levels for group_options_levels
     *
     * Checks the selected group_options keys for errors. Uses outer scope
     * `setError` if any.
     *
     */
    function formatSelectedGroupOptions(formData: DBFormData, groupLevels: {[key: string]: string[]}) {
        let levels: {[key: string]: string[]} = {};

        function findKeyIgnoreCase(key: string, obj: any): string | undefined {
            const lowerCaseKey = key?.toLowerCase();
            return Object.keys(obj).find(currentKey => currentKey.toLowerCase() === lowerCaseKey);
        }

        for (let option of formData.group_options) {
            let normalizedKey = option.name.toLowerCase().replace(/ /g, '_');
            let originalKey = findKeyIgnoreCase(normalizedKey, groupLevels);
            if (!originalKey) {
                setError(`"${normalizedKey}" is not a valid option. Please choose a different option.`);
                return;
            }
            // uses all levels
            const correspondingValues = groupLevels[originalKey] || [];

            if (!correspondingValues.length) {
                setError(`No levels found for variable "${option.name}".`);
                return;
            }

            levels[originalKey] = correspondingValues;
        }

        setFormData({...formData, selected_group_options: levels});
    }

    /**
     * Calls group-options BFF endpoint to figure out what group options are
     * available for the selected samples.
     * Taken directly from RNA analysis wizard
     */
    const getGroupOptions = async () => {
        const endpoint = `${apiHost}/resources/group-options`;
        const method = 'POST';

        const body = JSON.stringify({
            inputs: formData.sample_ids,
            minSamplesPerGroupNum: 2
        });
        const response = await fetch(endpoint, {
            method: method,
            headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: body
        });
        const responseData = await response.json();

        if (response.status === 200) {
            setGroupLevels(responseData?.levels);
            setStep(2);
            setError('');
            return true;
        }
        if (response.status === 400) {
            setError(responseData?.Error || responseData);
            return false;
        }
        if (responseData.Error) {
            setError(responseData.Error);
            return false;
        }
    };

    const fetchDefaults = async (experiment_id: string, apiHost: string) => {
        try {
            const accessToken = await getAccessTokenSilently();
            const data = await defaultChIPAnalysisbyExperiment(experiment_id, apiHost, accessToken);

            setDefaultChIPOptions(data);
            setError('');
        } catch (err) {
            console.error('Error fetching default ChIP analyses:', err);
            setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    const fetchPeaks = async (analysis_id: string, apiHost: string) => {
        try {
            const accessToken = await getAccessTokenSilently();
            const data = await peakCallingsData(analysis_id, apiHost, accessToken);
            // in event sample peaks are not found, assign to zero
            samplesD.forEach((item: any) => {
                item.peaks = data[item.id] ?? 0;
            });
            if (error) setError('');
        } catch (err) {
            console.error('Error loading peaks:', err);
            setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };

    // only call once on mount?
    useEffect(() => {
        fetchDefaults(experiment.id, apiHost);
    }, []);

    // only call when default_ChIP_analysis changes?
    // When user changes/selects default chip choice, all form data is reset + fetch peaks is called
    useEffect(() => {
        if (!formData.default_ChIP_analysis) return;
        fetchPeaks(formData.default_ChIP_analysis, apiHost);

        setFormData(prev => ({
            ...prev,
            sample_ids: [],
            group_options: [],
            selected_group_options: {},
            differential_binding_analysis: {
                significance_threshold: 0.05,
                summits: 200,
                tss_region: 3000,
                level: 'transcript',
                overlap: 'tss',
                genomic_annotation_priority: ['Promoter', '5UTR', '3UTR', 'Exon', 'Intron', 'Downstream', 'Intergenic']
            }
        }));
        setGroupLevels({});
    }, [formData.default_ChIP_analysis]);

    const handleContinueClick = async (stepIndex: number) => {
        if (stepIndex === 1) {
            // // when user moves to stepIndex 1 (sample Chooser), clear group_options. might be better to clear only if samples change?
            setFormData(prev => ({
                ...prev,
                group_options: [],
                selected_group_options: {}
            }));
            setGroupLevels({});

            // if getGroupOptions gives error (returns false) prevent wizard from moving past sample chooser (user should rechoose samples if no groups are found/valid)
            const ok = await getGroupOptions();
            if (!ok) return;
        } else if (stepIndex === 2) {
            formatSelectedGroupOptions(formData, groupLevels);
        }
        if (stepIndex < steps.length - 1) {
            setStep(stepIndex + 1);
        } else {
            console.log('Final step reached, ready to save analysis:', formData);
            setIsSubmitting(true);
            try {
                const accessToken = await getAccessTokenSilently();
                const expandedSampleIds = expandSampleSelection(formData.sample_ids, samplesD);
                const updatedFormData = {
                    ...formData,
                    sample_ids: expandedSampleIds
                };
                const analysis = await createAnalysis(experiment, updatedFormData, apiHost, accessToken);
                const _startresponse = await startAnalysis(experiment.id, analysis.id, updatedFormData, apiHost, accessToken);
                navigate(`/experiment/${experiment.id}`);
            } catch (err) {
                console.error('Error creating analysis:', err);
                setError(`${err instanceof Error ? err.message : 'Unknown error'}`);
                setIsSubmitting(false);
            }
        }
    };

    const steps: Step[] = [
        {
            name: 'Analysis details',
            caption: 'Name your analysis, choose default ChIP analysis, and provide a description',
            description: 'Name your analysis, choose default ChIP analysis, and optionally provide a description.',
            component: (
                <ChIPAnalysisDownStreamStep0
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    error={error}
                    ChIPDefaultAnalysisList={defaultChIPOptions}
                />
            ),
            icon: <AssignmentOutlined sx={{fill: step === 0 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Select samples',
            caption: 'Indicate which samples you want to include in your analysis.',
            description:
                'Indicate which samples should be included for analysis. You can edit samples in Manage Samples. For a sample to be usable in Differential Binding Analysis, sample peaks must be greater than 1000',
            component: (
                <ExpandableTableChip
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    data={samplesD}
                    error={error}
                    setError={setError}
                    autoExpandSelection={true}
                />
            ),
            icon: <IntegrationInstructionsOutlined sx={{fill: step === 1 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Select variables',
            caption: 'Designate variables to compare in this analysis.',
            description: 'Select up to two variables for comparison in this analysis. ',
            component: (
                <SelectVariabledDBA
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    groupLevels={groupLevels}
                    error={error}
                    setError={setError}
                />
            ),
            icon: <ListAltOutlined sx={{fill: step === 2 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Edit analysis parameter settings',
            caption: 'Advanced configuration parameters for analysis.',
            description: 'Customize options for data analysis or use our default configuration.',
            component: <DifferentialBindingParameters formData={formData} setFormData={setFormData} setValidForm={setAbleToSave} />,
            icon: <SettingsOutlined sx={{fill: step === 3 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Review analysis',
            caption: 'Check your setup before submitting for analysis.',
            description:
                'Confirm your analysis setup. Once an analysis has been started, its settings cannot be changed. If you want to run variations of this analysis, you will need to go through the setup process again.',
            component: <DBReview formData={formData} setFormData={setFormData} handleNext={setStep} samples={samplesD} />,
            icon: <CheckCircleOutlineRounded sx={{fill: step === 4 ? 'url(#linearColors)' : ''}} />
        }
    ];

    // remove error message when user jumps between steps using side panel (might be uneeded/ better to add to CreationFlowBar?)
    useEffect(() => {
        setError('');
    }, [step]);

    return (
        <>
            <CreationFlowBar title="SETUP NEW ANALYSIS" steps={steps} step={step} setStep={setStep} />
            <FormContainer>
                <Typography variant="headline" size="large">
                    {steps[step].name}
                </Typography>
                <Typography variant="body" size="medium" mb={2}>
                    {steps[step].description}
                </Typography>
                {steps[step].component}
                {error && <ErrorComponent message={error} />}
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={() => handleContinueClick(step)}
                    disabled={!ableToSave}
                    loading={isSubmitting}>
                    {step !== 4 ? 'Continue' : 'Run analysis'}
                </LoadingButton>
            </FormContainer>
        </>
    );
}
